"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { Chip } from "@heroui/chip";

interface ActiveFiltersProps {
  currentFilters: {
    search?: string;
    countrySlug?: string;
    countryName?: string;
    citySlug?: string;
    cityName?: string;
    month?: string;
    year?: string;
    type?: "upcoming" | "past" | "all";
  };
}

const months = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

export default function ActiveFilters({ currentFilters }: ActiveFiltersProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Helper function to encode names for URLs
  const encodeNameForUrl = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  };

  // Helper function to build URL when removing filters
  const buildUrlWithoutFilter = (filterToRemove: string) => {
    let basePath = "/events";

    // Build path structure using slugs (country/city) - skip the filter being removed
    if (currentFilters.countrySlug && filterToRemove !== "country") {
      basePath += `/country/${currentFilters.countrySlug}`;

      if (currentFilters.citySlug && filterToRemove !== "city") {
        basePath += `/city/${currentFilters.citySlug}`;
      }
    }

    // Add remaining query parameters (skip the filter being removed)
    const params = new URLSearchParams();
    if (currentFilters.year && filterToRemove !== "year")
      params.set("year", currentFilters.year);
    if (
      currentFilters.type &&
      currentFilters.type !== "upcoming" &&
      filterToRemove !== "type"
    )
      params.set("type", currentFilters.type);
    if (currentFilters.month && filterToRemove !== "month")
      params.set("month", currentFilters.month);
    if (currentFilters.search && filterToRemove !== "search")
      params.set("search", currentFilters.search);

    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  const removeFilter = (key: string) => {
    // Special handling for city when country is removed
    if (key === "country") {
      // Remove both country and city, go back to main events page
      const params = new URLSearchParams();
      if (currentFilters.year) params.set("year", currentFilters.year);
      if (currentFilters.type && currentFilters.type !== "upcoming")
        params.set("type", currentFilters.type);
      if (currentFilters.month) params.set("month", currentFilters.month);
      if (currentFilters.search) params.set("search", currentFilters.search);

      // Keep filters panel open
      params.set("filters", "on");

      const queryString = params.toString();
      const newUrl = `/events?${queryString}`;
      router.push(newUrl, { scroll: false });
    } else {
      // For other filters, build URL without that specific filter
      const newUrl = buildUrlWithoutFilter(key);
      router.push(`${newUrl}${newUrl.includes("?") ? "&" : "?"}filters=on`, {
        scroll: false,
      });
    }
  };

  const hasActiveFilters =
    currentFilters.search ||
    currentFilters.countryName ||
    currentFilters.cityName ||
    currentFilters.year ||
    currentFilters.month ||
    (currentFilters.type && currentFilters.type !== "upcoming");

  if (!hasActiveFilters) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {currentFilters.search && (
        <Chip
          onClose={() => removeFilter("search")}
          variant="flat"
          color="primary"
        >
          Search: {currentFilters.search}
        </Chip>
      )}
      {currentFilters.countryName && (
        <Chip
          onClose={() => removeFilter("country")}
          variant="flat"
          color="primary"
        >
          {currentFilters.countryName}
        </Chip>
      )}
      {currentFilters.cityName && (
        <Chip
          onClose={() => removeFilter("city")}
          variant="flat"
          color="primary"
        >
          {currentFilters.cityName}
        </Chip>
      )}
      {currentFilters.year && (
        <Chip
          onClose={() => removeFilter("year")}
          variant="flat"
          color="primary"
        >
          {currentFilters.year}
        </Chip>
      )}
      {currentFilters.month && (
        <Chip
          onClose={() => removeFilter("month")}
          variant="flat"
          color="primary"
        >
          {months.find((m) => m.value === currentFilters.month)?.label}
        </Chip>
      )}
      {currentFilters.type && currentFilters.type !== "upcoming" && (
        <Chip
          onClose={() => removeFilter("type")}
          variant="flat"
          color="primary"
        >
          {currentFilters.type}
        </Chip>
      )}
    </div>
  );
}
